import { useEffect, useRef, useState } from 'react';
import { autofocusAttr } from 'hooks/useAutofocusAttr';
import { AHU_TAG_PATTERN, buildNewAhuDefaultTag } from '../../ahuUtils';

interface NewAhuPopupProps {
  /** Create → POST {project_id, tag} with wait semantics (B1.5). */
  onCreate: (tag: string) => void;
  onClose: () => void;
}

/**
 * Shared New-AHU popup (`/static/mvc/new_ahu/` — view.ahus.new_ahu resolves
 * to the SHARED module per universal_app.js line 150, NOT the empty
 * project/view/new_ahu.js). Faithful copy (B1.5 / parity 146):
 * "Create new Air Handling Unit?", "AHU Tag:", pattern + minlength 3 /
 * maxlength 30, "Minimum 3 characters" note, default tag
 * `AH-{yy}{mm}{dd}_{hh}{mm}{ss}`, input focused + selected after 200ms,
 * keyup validity && length>2 gating Create (`.non-clickable` + `disabled`)
 * and toggling `.invalid-field` on the input (dev 9e92b36370,
 * new_ahu.js:95-108), <3 chars on Create → `.invalid-field`. List refresh +
 * auto-select arrive via the socket post path (no refetch here).
 *
 * Esc/Enter ride the global popup keys (button[name=cancel] /
 * button[autofocus=true] — new_ahu.tmpl carries the literal autofocus attr).
 */
const NewAhuPopup = ({ onCreate, onClose }: NewAhuPopupProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [tag, setTag] = useState(() => buildNewAhuDefaultTag());
  const [invalid, setInvalid] = useState(false);
  const [createLocked, setCreateLocked] = useState(false);

  // show(): default tag pre-filled; input focused + selected after 200ms.
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  // checkField (keyup): checkValidity() && length > 2 gates Create — invalid
  // adds `.non-clickable` + `disabled` on the button AND `.invalid-field` on
  // the input; valid reverses all three (new_ahu.js:95-108).
  const checkField = () => {
    const inp = inputRef.current;
    const valid = (inp?.checkValidity() ?? false) && (inp?.value.length ?? 0) > 2;
    setCreateLocked(!valid);
    setInvalid(!valid);
  };

  const create = () => {
    if (tag === undefined) return;
    if (tag.length < 3) {
      setInvalid(true);
      return;
    }
    onCreate(tag);
    onClose();
  };

  return (
    <div className="popup-wrapper create-popup active">
      <div className="popup-content">
        <h2 className="popup-heading">Create new Air Handling Unit?</h2>
        <div>
          <div className="clearfix popup-content-part">
            <span>AHU Tag:</span>
            <input
              ref={inputRef}
              type="text"
              name="name"
              className={`ahu-name${invalid ? ' invalid-field' : ''}`}
              minLength={3}
              maxLength={30}
              pattern={AHU_TAG_PATTERN}
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              onKeyUp={checkField}
            />
            <i
              className="text-clear icon-close-1"
              onClick={() => {
                setTag('');
                // empty fails the new length>2 rule: disable Create + red border.
                setCreateLocked(true);
                setInvalid(true);
              }}
            />
            <span className="validation-note">Minimum 3 characters</span>
          </div>
          <div className="popup-control-buttons">
            <button
              ref={autofocusAttr}
              className={`add highlighted-btn${createLocked ? ' non-clickable' : ''}`}
              name="add_new_ahu"
              // disabled also blocks the global popup-keys Enter (a disabled
              // button ignores .click()), matching "Enter must not create".
              disabled={createLocked}
              onClick={create}
            >
              Create
            </button>
            <button
              className="default-btn not-highlighted-btn"
              name="cancel"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewAhuPopup;
